import type { FC, ReactNode } from 'react'

import {
    ArrowDownRight,
    Clock,
    Mail,
    MapPin,
    Phone
} from 'lucide-react'
import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import { FinalCta } from '@/components/kit/final-cta'
import { PlatformIcon } from '@/components/kit/platform-icon'
import { Reveal } from '@/components/kit/reveal'
import { SectionLabel } from '@/components/kit/section-label'
import { SplitText } from '@/components/kit/split-text'
import { FaqSection } from '@/components/home/home-faq'
import { BookCall } from '@/components/contact/book-call'
import { ContactForm } from '@/components/contact/contact-form'
import { siteConfig } from '@/constants/site'

const nextSteps = [
    { stamp: 'Within 24 hours', title: 'A real reply', body: 'A strategist reads your note, looks at your feed and writes back with first thoughts. No autoresponder.' },
    { stamp: 'Within 48 hours', title: 'Your free forecast', body: 'One page on where your category is heading, three ideas built for it, and the apps worth your time.' },
    { stamp: 'When you are ready', title: 'A plan that fits', body: 'If you like the forecast, we shape a plan around it. If not, the forecast is still yours to keep.' }
]

const contactFaqs = [
    { question: 'Do I need a big budget?', answer: 'No. Starter is built for small brands finding their voice on one or two apps. We will tell you honestly if you are better off doing it yourself for now.' },
    { question: 'What should I put in the message?', answer: 'What you sell, who buys it and what you would love people to say about you. Links to your accounts help. Perfect grammar does not matter.' },
    { question: 'Can I just ask a question?', answer: 'Of course. Not every message has to turn into a project. We answer curious emails too.' }
]

const Contact: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Contact'
                description="Tell Updraft about your brand and get a free forecast. We reply within 24 hours, or book a free 20 minute call with a strategist."
            />

            <section
                aria-labelledby='contact-title'
                className='relative overflow-hidden pb-14 pt-36 md:pt-44'
            >
                <div
                    aria-hidden='true'
                    className='bg-grid mask-radial absolute inset-0'
                />

                <div className='relative mx-auto max-w-[1400px] px-5 md:px-10'>
                    <SectionLabel>Contact · clear for take-off</SectionLabel>
                    <SplitText
                        as='h1'
                        id='contact-title'
                        lines={["Let's get you", 'off the ground']}
                        italic='ground'
                        highlight='off'
                        className='mt-6 max-w-[14ch] text-[clamp(3rem,8.4vw,7.75rem)] font-bold leading-[0.88] tracking-[-0.045em] md:max-w-none'
                    />

                    <div className='mt-14 grid gap-6 lg:grid-cols-12 lg:gap-8'>
                        <Reveal
                            delay={200}
                            className='lg:col-span-7'
                        >
                            <ContactForm />
                        </Reveal>

                        <Reveal
                            delay={300}
                            className='flex flex-col gap-4 lg:col-span-5'
                        >
                            <div className='rounded-[2rem] border border-black/5 bg-paper p-7 md:p-8'>
                                <p className='font-mono text-[11px] uppercase tracking-[0.16em] text-stone'>Find us</p>
                                <ul className='mt-6 flex flex-col gap-5'>
                                    <li>
                                        <a
                                            href={`mailto:${siteConfig.email}`}
                                            className='group flex items-center gap-4'
                                        >
                                            <span className='flex size-11 shrink-0 items-center justify-center rounded-full bg-sand transition-colors group-hover:bg-ember'>
                                                <Mail className='size-[18px]' />
                                            </span>
                                            <span>
                                                <span className='block text-sm text-stone'>Email</span>
                                                <span className='block text-lg font-semibold'>{siteConfig.email}</span>
                                            </span>
                                        </a>
                                    </li>
                                    <li>
                                        <a
                                            href={siteConfig.phoneHref}
                                            className='group flex items-center gap-4'
                                        >
                                            <span className='flex size-11 shrink-0 items-center justify-center rounded-full bg-sand transition-colors group-hover:bg-ember'>
                                                <Phone className='size-[18px]' />
                                            </span>
                                            <span>
                                                <span className='block text-sm text-stone'>Phone</span>
                                                <span className='block text-lg font-semibold'>{siteConfig.phone}</span>
                                            </span>
                                        </a>
                                    </li>
                                    <li className='flex items-center gap-4'>
                                        <span className='flex size-11 shrink-0 items-center justify-center rounded-full bg-sand'>
                                            <MapPin className='size-[18px]' />
                                        </span>
                                        <span>
                                            <span className='block text-sm text-stone'>The print shop</span>
                                            <span className='block text-lg font-semibold'>{siteConfig.address}</span>
                                        </span>
                                    </li>
                                </ul>
                            </div>

                            <div className='flex items-center gap-4 rounded-[2rem] border border-black/5 bg-paper p-7 md:p-8'>
                                <span className='relative flex size-12 shrink-0 items-center justify-center rounded-full bg-ink text-cream'>
                                    <Clock className='size-5' />
                                    <span className='absolute -right-0.5 -top-0.5 size-3 animate-pulse-dot rounded-full bg-ember' />
                                </span>
                                <div>
                                    <p className='font-heading text-2xl font-bold tracking-[-0.04em]'>We reply within 24 hours</p>
                                    <p className='mt-1 text-sm text-stone'>Usually much faster. Monday to Friday, 9am to 6pm, London time.</p>
                                </div>
                            </div>

                            <div className='rounded-[2rem] border border-black/5 bg-paper p-7 md:p-8'>
                                <p className='font-mono text-[11px] uppercase tracking-[0.16em] text-stone'>Watch us work</p>
                                <ul className='mt-5 flex flex-wrap gap-2'>
                                    {siteConfig.socials.map(social => (
                                        <li key={social.platform}>
                                            <a
                                                href={social.href}
                                                target='_blank'
                                                rel='noopener noreferrer'
                                                className='inline-flex items-center gap-2 rounded-full border border-ink/12 px-4 py-2.5 text-sm font-medium transition-colors hover:border-ink hover:bg-ink hover:text-cream'
                                            >
                                                <PlatformIcon
                                                    name={social.platform}
                                                    className='size-4'
                                                />
                                                {social.label}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <a
                                href='#book-a-call'
                                className='group grain relative flex flex-1 flex-col justify-between gap-10 overflow-hidden rounded-[2rem] bg-ember p-7 text-ink [--grain-opacity:0.18] md:p-8'
                            >
                                <span className='font-mono text-[11px] uppercase tracking-[0.16em] text-ink/75'>Rather hear a voice?</span>
                                <span className='flex items-end justify-between gap-6'>
                                    <span className='font-heading text-[2rem] font-bold leading-[0.95] tracking-[-0.035em]'>
                                        Book a free 20 minute call
                                    </span>
                                    <span className='flex size-12 shrink-0 items-center justify-center rounded-full bg-ink text-cream transition-transform duration-500 ease-expo group-hover:translate-y-1'>
                                        <ArrowDownRight className='size-5' />
                                    </span>
                                </span>
                            </a>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section
                aria-labelledby='next-steps-title'
                className='mx-auto max-w-[1400px] px-5 py-20 md:px-10 md:py-28'
            >
                <SectionLabel>What happens next</SectionLabel>
                <SplitText
                    as='h2'
                    id='next-steps-title'
                    lines={['Three steps,', 'no runway delays']}
                    italic='no'
                    className='mt-5 text-[clamp(2.5rem,5.6vw,5.25rem)] font-bold leading-[0.9] tracking-[-0.045em]'
                />
                <ol className='mt-12 grid gap-5 md:grid-cols-3'>
                    {nextSteps.map((step, index) => (
                        <Reveal
                            as='li'
                            key={step.title}
                            delay={index * 100}
                            settle={[-1.2, 0.8, -0.6][index]}
                        >
                            <div className='relative h-full rounded-[1.75rem] border border-ink/10 bg-paper p-7'>
                                <span
                                    aria-hidden='true'
                                    className='tape -top-3 left-8 rotate-2'
                                />
                                <p className='font-mono text-xs uppercase tracking-[0.16em] text-ember'>{String(index + 1).padStart(2, '0')} · {step.stamp}</p>
                                <h3 className='mt-4 text-3xl font-bold tracking-[-0.04em]'>{step.title}</h3>
                                <p className='mt-3 text-base leading-relaxed text-stone'>{step.body}</p>
                            </div>
                        </Reveal>
                    ))}
                </ol>
            </section>

            <BookCall />

            <FaqSection
                items={contactFaqs}
                id='contact-faq'
                lines={['Before you', 'hit send']}
            />

            <FinalCta
                lines={['Still thinking', 'about it?']}
                italic='thinking'
                body='Fair enough. Book twenty minutes with a strategist instead. No slides, no pressure, one useful idea guaranteed.'
                action='Book a free 20 minute call'
                href='#book-a-call'
            />
        </Layout>
    )
}

export default Contact
